/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Select } from 'antd';
import {
  UFS,
  listCitiesByUf,
  normalizeText,
  validateNaturality,
} from './naturality';

const { Option } = Select;

const filterOption = (input, option) => normalizeText(String(option?.label ?? ''))
  .includes(normalizeText(input));

// "Cidade/UF" → { city, uf }. Valor sem "/" (digitado à mão antes do GYR-1883)
// não tem UF e, por isso, não preenche nenhum dos dois seletores.
const parseValue = (value) => {
  if (typeof value !== 'string') {
    return {};
  }
  const index = value.lastIndexOf('/');
  if (index < 0) {
    return {};
  }
  return {
    city: value.slice(0, index).trim() || undefined,
    uf: value.slice(index + 1).trim().toUpperCase() || undefined,
  };
};

/**
 * Naturalidade como UF (lista) + Cidade (busca filtrada pela UF), gravando
 * `Cidade/UF` (GYR-1883, RN8.1). Controlado pelo Form.Item: `value` e `onChange`
 * trabalham com a string final, então o formulário continua com um único campo
 * `naturalityCity`. Enquanto a cidade não for escolhida o valor é `undefined` e
 * a regra "obrigatório" do Item barra o salvamento.
 */
const NaturalityField = ({
  value, onChange, disabled, id,
}) => {
  const parsed = parseValue(value);
  // Só mostra a cidade quando o valor gravado é válido (inclui grafia antiga já
  // gravada e variações como "Porto Alegre - RS"); texto livre inválido aparece
  // como campo vazio para ser refeito.
  const validated = validateNaturality(value);
  const valueUf = validated.ok ? validated.uf : parsed.uf;
  const [uf, setUf] = useState(valueUf);

  useEffect(() => {
    if (valueUf) {
      setUf(valueUf);
    }
  }, [valueUf]);

  const selectedCity = validated.ok ? validated.city : undefined;

  const cities = useMemo(() => {
    const list = listCitiesByUf(uf);
    return selectedCity && !list.includes(selectedCity) && validated.uf === uf
      ? [selectedCity, ...list]
      : list;
  }, [uf, selectedCity, validated.uf]);

  return (
    <Row gutter={8} wrap={false}>
      <Col flex="124px">
        <Select
          id={id ? `${id}-uf` : undefined}
          className="gyramais-input"
          showSearch
          placeholder="UF"
          disabled={disabled}
          value={uf}
          filterOption={filterOption}
          onChange={(nextUf) => {
            setUf(nextUf);
            onChange?.(undefined);
          }}
        >
          {UFS.map((item) => (
            <Option key={item} value={item} label={item}>{item}</Option>
          ))}
        </Select>
      </Col>
      <Col flex="auto">
        <Select
          id={id ? `${id}-city` : undefined}
          className="gyramais-input"
          showSearch
          placeholder={uf ? 'Cidade' : 'Escolha a UF primeiro'}
          disabled={disabled || !uf}
          value={selectedCity && validated.uf === uf ? selectedCity : undefined}
          filterOption={filterOption}
          onChange={(city) => onChange?.(city ? `${city}/${uf}` : undefined)}
        >
          {cities.map((city) => (
            <Option key={city} value={city} label={city}>{city}</Option>
          ))}
        </Select>
      </Col>
    </Row>
  );
};

/**
 * Regras do Form.Item: obrigatório (a menos que `required: false`) e valor no
 * formato/UF/município válidos, com o erro no próprio campo citando a pessoa
 * (RN8). `getPersonName` recebe `getFieldValue` do formulário.
 */
const naturalityRules = ({ required = true, getPersonName = () => undefined } = {}) => [
  required && {
    required: true,
    message: 'Por favor preencha o campo naturalidade.',
  },
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (value === undefined || value === null || value === '') {
        return Promise.resolve();
      }
      const result = validateNaturality(value, getPersonName(getFieldValue));
      return result.ok ? Promise.resolve() : Promise.reject(new Error(result.message));
    },
  }),
].filter(Boolean);

export { naturalityRules };
export default NaturalityField;
